import { cardOf, getStages, getWonder } from './data/registry';
import type { PublicCity } from './perspective';
import type { Effect } from './types/effects';
import type { Resource } from './types/resources';
import { RESOURCES } from './types/resources';

/** Una unidad de producción por turno; `options.length > 1` es una carta «/». */
export interface Slot {
  options: Resource[];
}

function slotsFromProduce(e: Extract<Effect, { kind: 'produce' }>): Slot[] {
  if (e.choice) return [{ options: [...e.resources] }];
  return e.resources.map((r) => ({ options: [r] }));
}

/** Efectos de las etapas construidas. */
export function builtStageEffects(c: PublicCity): Effect[] {
  if (!c.side) return [];
  return getStages(c.wonder, c.side)
    .slice(0, c.stagesBuilt)
    .flatMap((s) => s.effects);
}

/** Todos los efectos activos: cartas construidas + etapas. */
export function activeEffects(c: PublicCity): Effect[] {
  return [...c.city.flatMap((iid) => cardOf(iid).effects), ...builtStageEffects(c)];
}

export function hasEffect(c: PublicCity, kind: Effect['kind']): boolean {
  return activeEffects(c).some((e) => e.kind === kind);
}

/** Producción que el propio jugador puede usar: recurso inicial, cartas (incl. amarillas) y etapas. */
export function ownSlots(c: PublicCity): Slot[] {
  const slots: Slot[] = [{ options: [getWonder(c.wonder).resource] }];
  for (const iid of c.city) {
    for (const e of cardOf(iid).effects) if (e.kind === 'produce') slots.push(...slotsFromProduce(e));
  }
  for (const e of builtStageEffects(c)) if (e.kind === 'produce') slots.push(...slotsFromProduce(e));
  return slots;
}

/** Producción que los vecinos pueden comprar: recurso inicial y cartas marrones y grises. */
export function tradeableSlots(c: PublicCity): Slot[] {
  const slots: Slot[] = [{ options: [getWonder(c.wonder).resource] }];
  for (const iid of c.city) {
    const def = cardOf(iid);
    if (def.color !== 'brown' && def.color !== 'grey') continue;
    for (const e of def.effects) if (e.kind === 'produce') slots.push(...slotsFromProduce(e));
  }
  return slots;
}

export type PriceTable = Record<Resource, number>;

/** Precio por recurso comprado a cada vecino (2 por defecto, 1 con descuentos). */
export function tradePrices(c: PublicCity): { left: PriceTable; right: PriceTable } {
  const left = Object.fromEntries(RESOURCES.map((r) => [r, 2])) as PriceTable;
  const right = { ...left };
  for (const e of activeEffects(c)) {
    if (e.kind !== 'tradePrice') continue;
    for (const side of e.sides) {
      const table = side === 'left' ? left : right;
      for (const r of e.resources) table[r] = 1;
    }
  }
  return { left, right };
}

export function shieldsOf(c: PublicCity): number {
  let n = 0;
  for (const e of activeEffects(c)) if (e.kind === 'shields') n += e.amount;
  return n;
}
